import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  createConnectAccount,
  getAccountStatus,
  activatePayments,
  deactivatePayments,
  resetConnectAccount,
} from '../api/stripe.api';
import connectCountries from '@/config/shared/stripe-connect-countries.json';

const supportedCountryCodes = new Set(connectCountries.map(({ code }) => code));

const countryName = code =>
  connectCountries.find(country => country.code === code)?.name || code;

/**
 * Pick a sensible default for the country picker: the country saved on the
 * user from an earlier attempt, else the browser's region, else nothing.
 * The user always confirms it explicitly before an account is created.
 */
function guessInitialCountry(currentUser) {
  const persisted = currentUser?.stripeConnectCountry?.toUpperCase();
  if (persisted && supportedCountryCodes.has(persisted)) {
    return persisted;
  }

  const browserLanguage =
    typeof navigator !== 'undefined' ? navigator.language || '' : '';
  const region = browserLanguage.split(/[-_]/)[1]?.toUpperCase();
  return region && supportedCountryCodes.has(region) ? region : '';
}

export default function StripeOnboarding({ currentUser, onComplete }) {
  const [loading, setLoading] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  const [error, setError] = useState(null);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [togglingPayments, setTogglingPayments] = useState(false);
  const [toggleMessage, setToggleMessage] = useState(null);
  const [country, setCountry] = useState(() =>
    guessInitialCountry(currentUser),
  );
  const [resetting, setResetting] = useState(false);

  const isMountedRef = useRef(true);
  const hasTriggeredCompleteRef = useRef(
    Boolean(currentUser?.stripeOnboardingComplete),
  );

  const renderButtonSpinner = label => (
    <>
      <span
        className="spinner-border spinner-border-sm me-2"
        role="status"
        aria-hidden="true"
      ></span>
      {label}
    </>
  );

  const loadAccountStatus = useCallback(async () => {
    try {
      const status = await getAccountStatus();

      if (!isMountedRef.current) {
        return;
      }

      setAccountStatus(status);
      setError(null);

      if (status?.onboardingComplete) {
        if (!hasTriggeredCompleteRef.current && onComplete) {
          hasTriggeredCompleteRef.current = true;
          onComplete();
        }
      } else {
        hasTriggeredCompleteRef.current = false;
      }
    } catch (err) {
      if (!isMountedRef.current) {
        return;
      }
      const errorMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load account status';

      setAccountStatus({
        hasAccount: false,
        onboardingComplete: false,
        error: errorMessage,
      });
      setError(errorMessage);
    }
  }, [onComplete]);

  const refreshStatus = useCallback(async () => {
    setRefreshingStatus(true);
    try {
      await loadAccountStatus();
    } finally {
      if (isMountedRef.current) {
        setRefreshingStatus(false);
      }
    }
  }, [loadAccountStatus]);

  useEffect(() => {
    isMountedRef.current = true;
    loadAccountStatus();

    return () => {
      isMountedRef.current = false;
    };
  }, [currentUser, loadAccountStatus]);

  useEffect(() => {
    if (currentUser?.stripeOnboardingComplete) {
      hasTriggeredCompleteRef.current = true;
    }
  }, [currentUser?.stripeOnboardingComplete]);

  const openHostedOnboarding = useCallback(async () => {
    // A country is only needed when an account has to be created; Stripe
    // fixes it at that point and ignores it afterwards.
    const needsCountry = !accountStatus?.hasAccount;
    if (needsCountry && !country) {
      setError('Please choose your country before connecting with Stripe.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await createConnectAccount(
        needsCountry ? country : undefined,
      );

      if (result.onboardingComplete) {
        await refreshStatus();
        return;
      }

      if (result.url) {
        window.location.href = result.url;
      } else {
        setError('Failed to generate onboarding link.');
      }
    } catch (err) {
      const data = err?.response?.data;
      // Prefer the reason Stripe gave over our generic label, so the host can
      // tell an unsupported country apart from a platform misconfiguration.
      setError(
        data?.details ||
          data?.message ||
          data?.error ||
          err?.message ||
          'Failed to open Stripe onboarding.',
      );
    } finally {
      setLoading(false);
    }
  }, [accountStatus?.hasAccount, country, refreshStatus]);

  const handleStartOver = useCallback(async () => {
    const currentCountry = accountStatus?.country
      ? countryName(accountStatus.country)
      : 'the current country';

    if (
      !window.confirm(
        `Start over with a new Stripe account?\n\nThe unfinished account for ${currentCountry} will be forgotten and you can choose a different country. Anything you already entered in Stripe will need to be entered again.`,
      )
    ) {
      return;
    }

    try {
      setResetting(true);
      setError(null);
      await resetConnectAccount();
      // Let the user pick afresh rather than silently reusing the old guess
      setCountry('');
      await refreshStatus();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to reset the Stripe account.',
      );
    } finally {
      if (isMountedRef.current) {
        setResetting(false);
      }
    }
  }, [accountStatus?.country, refreshStatus]);

  const handleTogglePayments = useCallback(async () => {
    const isCurrentlyActive = accountStatus?.paymentsActive;

    try {
      setTogglingPayments(true);
      setToggleMessage(null);

      const result = isCurrentlyActive
        ? await deactivatePayments()
        : await activatePayments();

      if (result.success) {
        setToggleMessage({
          type: 'success',
          text: result.message,
        });

        // Refresh status to get updated paymentsActive value
        await refreshStatus();
      } else {
        throw new Error(result.message || 'Failed to toggle payments');
      }
    } catch (err) {
      setToggleMessage({
        type: 'danger',
        text:
          err?.response?.data?.message ||
          err?.message ||
          'Failed to update payment status',
      });
    } finally {
      setTogglingPayments(false);

      // Clear message after 5 seconds
      setTimeout(() => {
        if (isMountedRef.current) {
          setToggleMessage(null);
        }
      }, 5000);
    }
  }, [accountStatus?.paymentsActive, refreshStatus]);

  if (!accountStatus) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="text-muted small mt-2">Loading account status...</p>
      </div>
    );
  }

  // Show error state if API call failed
  if (accountStatus.error && !accountStatus.hasAccount) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="alert alert-warning mb-0">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>Unable to load Stripe account status</strong>
            <p className="mb-0 mt-2 small">{accountStatus.error}</p>
            <button
              className="btn btn-sm btn-primary mt-2"
              onClick={() => {
                setAccountStatus(null);
                // Retry loading
                getAccountStatus()
                  .then(status => setAccountStatus(status))
                  .catch(err => {
                    setAccountStatus({
                      hasAccount: false,
                      onboardingComplete: false,
                      error:
                        err?.response?.data?.error ||
                        err?.message ||
                        'Failed to load account status',
                    });
                  });
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Already fully onboarded
  if (accountStatus.onboardingComplete) {
    const isActive = accountStatus.paymentsActive !== false; // Default to true

    return (
      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h5 className="card-title mb-2">
                <i className="fas fa-credit-card me-2"></i>
                Payment Account
              </h5>
              {/* <p className="text-muted mb-0">
                Account ID: <code>{accountStatus.accountId}</code>
              </p> */}
            </div>
            <span
              className={`badge ${isActive ? 'bg-success' : 'bg-secondary'}`}
            >
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </div>

          <div className="alert alert-success mb-3">
            <i className="fas fa-check-circle me-2"></i>
            <strong>Stripe Connect Setup Complete</strong>
            <p className="mb-0 mt-2">
              Your account is verified and ready to receive direct payouts.
            </p>
          </div>

          {toggleMessage && (
            <div className={`alert alert-${toggleMessage.type} mb-3`}>
              <i
                className={`fas ${
                  toggleMessage.type === 'success'
                    ? 'fa-check-circle'
                    : 'fa-exclamation-circle'
                } me-2`}
              ></i>
              {toggleMessage.text}
            </div>
          )}

          <div className="d-flex flex-column gap-2">
            <p className="mb-2">
              <strong>Payment Status:</strong>{' '}
              {isActive ? (
                <span className="text-success">
                  <i className="fas fa-check-circle me-1"></i>
                  Accepting payments
                </span>
              ) : (
                <span className="text-muted">
                  <i className="fas fa-pause-circle me-1"></i>
                  Payments paused
                </span>
              )}
            </p>

            <button
              className={`btn ${
                isActive ? 'btn-outline-secondary' : 'btn-primary'
              }`}
              onClick={handleTogglePayments}
              disabled={togglingPayments}
            >
              {togglingPayments ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {isActive ? 'Deactivating...' : 'Activating...'}
                </>
              ) : (
                <>
                  <i
                    className={`fas ${isActive ? 'fa-pause' : 'fa-play'} me-2`}
                  ></i>
                  {isActive ? 'Deactivate Payments' : 'Activate Payments'}
                </>
              )}
            </button>

            <p className="text-muted small mb-0 mt-2">
              {isActive
                ? 'Deactivate to temporarily stop accepting new bookings with paid reservations.'
                : 'Activate to start accepting paid bookings again.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const hasExistingAccount = accountStatus.hasAccount;
  const buttonLabel = hasExistingAccount
    ? 'Continue in Stripe'
    : 'Connect with Stripe';

  return (
    <div className="card">
      <div className="card-body">
        <h5 className="card-title">
          <i className="fas fa-credit-card me-2"></i>
          Connect Using Stripe
        </h5>
        <p className="card-text">
          {hasExistingAccount
            ? 'Finish the remaining steps in Stripe so payouts can start.'
            : 'Create your Stripe Express account to receive automatic payouts.'}
        </p>

        {hasExistingAccount ? (
          <div className="mb-3 small">
            {accountStatus.country && (
              <span className="text-muted">
                Account country: {countryName(accountStatus.country)}.{' '}
              </span>
            )}
            {!accountStatus.chargesEnabled && !accountStatus.payoutsEnabled && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 align-baseline"
                onClick={handleStartOver}
                disabled={resetting || loading}
              >
                {resetting
                  ? 'Resetting…'
                  : 'Wrong country? Start over with a new account'}
              </button>
            )}
          </div>
        ) : (
          <div className="mb-3">
            <label htmlFor="stripe-connect-country" className="form-label">
              Country where you live and bank
            </label>
            <select
              id="stripe-connect-country"
              className="form-select"
              value={country}
              onChange={event => setCountry(event.target.value)}
              disabled={loading}
            >
              <option value="">Choose a country…</option>
              {connectCountries.map(({ code, name }) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
            <div className="form-text">
              Stripe fixes the country when your account is created and it
              cannot be changed afterwards.
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger mb-3">
            <i className="fas fa-exclamation-circle me-2"></i>
            {error}
          </div>
        )}

        <div className="d-flex flex-column flex-md-row gap-2 mt-3">
          <button
            className="btn btn-primary"
            onClick={openHostedOnboarding}
            disabled={loading || (!hasExistingAccount && !country)}
          >
            {loading ? renderButtonSpinner('Opening Stripe...') : buttonLabel}
          </button>
          <button
            className="btn btn-link p-0"
            type="button"
            onClick={refreshStatus}
            disabled={refreshingStatus}
          >
            {refreshingStatus ? 'Checking status…' : 'Refresh status'}
          </button>
        </div>
      </div>
    </div>
  );
}

StripeOnboarding.propTypes = {
  currentUser: PropTypes.object.isRequired,
  onComplete: PropTypes.func,
};
